"use client";

import React, { useState } from "react";
import { NumberCard } from "@/components/Cards";
import Link from "next/link";
import { useLiveNumbers } from "@/lib/useLiveNumbers";

export default function FreeTempNumbersList({
  countryName,
  data,
  currentPage,
  countryContent,
  countryCode,
}) {
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  // Overlay the build-time list with the current inventory; the static data
  // stays as the SSR content and the fallback if the fetch fails.
  const live = useLiveNumbers(countryCode);
  const numbers = live?.freeNumbers?.length ? live.freeNumbers : data;

  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  const sortedData = [...numbers].sort((a, b) => {
    if (sortOrder === "newest") {
      return new Date(b.created_at) - new Date(a.created_at);
    } else if (sortOrder === "oldest") {
      return new Date(a.created_at) - new Date(b.created_at);
    } else {
      // Default sort (when sortOrder is empty or "Select")
      return 0;
    }
  });

  return (
    <>
      <div className="py-4 lg:py-14 space-y-6">
        <h1 className="font-bold text-3xl capitalize">
          Free Temporary Phone Number {countryName} — Receive SMS Online Free, Instantly
        </h1>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left column - Description text */}
          <div className="w-full lg:w-[58%] space-y-4">
            {countryContent?.contextParagraph && (
              <p className="text-lg font-medium text-gray-600">
                {countryContent.contextParagraph}
              </p>
            )}
            {countryContent?.valueParagraph && (
              <p className="text-lg font-medium text-gray-600">
                {countryContent.valueParagraph}
              </p>
            )}
            {countryContent?.ctaParagraph && (
              <p className="text-lg font-medium text-gray-600">
                {countryContent.ctaParagraph}
              </p>
            )}
            {countryContent?.ctaLine && (
              <p className="text-lg font-medium text-gray-600">
                {countryContent.ctaLine}
              </p>
            )}
          </div>

          {/* Right column - Message not received section */}
          <div className="text-sm space-y-4 p-8 text-gray-700 border-[#090EDB] border-[2px] w-full lg:w-[42%] rounded-xl h-auto">
            <p className="font-bold text-lg"> Message not received? </p>
            <p>
              Free {countryName} numbers are shared and can sometimes be blocked by certain platforms. For guaranteed delivery and full privacy, <Link href={`/rent-private-number/${currentPage}`} className="underline">rent your own dedicated private {countryName} virtual phone number</Link>. It works with all services, every time.
            </p>
            <div className="flex items-center justify-center">
              <Link href={`/rent-private-number/${currentPage}`}>
                <div
                  className={`bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:bg-gradient-to-tr hover:from-[#090EDB]/80 hover:to-[#E73DC4]/80 px-10 transition-all duration-300 hover:px-14 py-3 text-white font-semibold rounded-lg`}
                >
                  Rent Private Phone Number
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* search and sort */}
      <div className="py-8 flex lg:flex-row flex-col items-start lg:items-center justify-between lg:space-y-0 space-y-4">
        <div className="w-full lg:w-[22%] border-[2px] rounded-lg py-2 px-2 flex items-center space-x-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-6 h-6 text-gray-400"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
            />
          </svg>

          <input
            id="search_box"
            name="search_box"
            type="text"
            placeholder="Number Search"
            className="bg-transparent w-full outline-none"
            onChange={(e) => {
              // Only allow numbers in the search input
              const value = e.target.value.replace(/[^0-9]/g, '');
              setSearch(value);
            }}
            value={search}
          />
        </div>

        <div className="w-full lg:w-[20%] flex items-center space-x-2">
          <p className="text-sm font-medium text-gray-700">Sort by:</p>
          <div className="p-1  border bg-transparent rounded-lg">
            <select
              className="p-1 bg-transparent outline-none"
              onChange={handleSortChange}
              value={sortOrder}
            >
              <option value="">Select</option>
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>
          </div>
        </div>
      </div>

      <div className="py-8 ">
        <div className="gap-4  w-full h-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {sortedData
            ?.filter((val) => {
              // Convert search to string for comparison
              const searchTerm = search.toString().toLowerCase();
              const phoneNumber = val.number.toString();
              
              // If search is empty, return all items
              if (searchTerm === "") {
                return true;
              }
              
              // Check if phone number includes search term
              return phoneNumber.includes(searchTerm);
            })
            ?.map((number) => (
              <NumberCard
                key={number.id}
                country={`${number.countries.country_flag} ${number.countries?.country_name}`}
                status={number.status.toLowerCase()}
                number={`+${number.countries.country_tel_code}-${number.number}`}
                href={`/free-temporary-numbers/${currentPage}/${number.countries.country_tel_code}${number.number}`}
              />
            ))}
        </div>
      </div>
    </>
  );
}
